/**
 * cfmmc 第三方授权登录（OAuth2 授权码模式）核心封装
 *
 * 流程：
 * 1. 首次进入：调 getState 拿第三方授权地址，当前页跳转过去；
 * 2. 第三方认证完成后，重定向回我方页面，URL 上带 loginType=13&code=xxx&state=xxx；
 * 3. 用 code + state 调 callback 换本系统 token，写入本地缓存；
 * 4. 调用方拿到 token 后继续走原有登录成功流程（拉用户信息 -> 跳首页/目标页）。
 *
 * 暴露方法：
 * - openThirdPartyLogin  获取授权地址并跳转第三方
 * - fetchTokenByCallback 用回调参数换 token 并落盘
 * - handleCfmmcSsoLogin  一站式处理：已带 code 则换 token，否则跳转第三方
 */
import http from "@/plugins/http/http.js";
import common from "@/plugins/common.js";

// 与请求拦截器（src/plugins/http/interceptor.js）保持同一个 key
const TOKEN_KEY = "access_token";

/**
 * 有效字符串判断：空串、"null"、"undefined" 均视为无效
 */
function isValidStr(val) {
  return common.ifStrNotBlank(val);
}

/**
 * 构造带 tips / errMsg 的错误对象，方便页面层直接取到展示文案
 */
function createError(message) {
  const err = new Error(message);
  err.tips = message;
  err.errMsg = message;
  return err;
}

/**
 * 保留后端返回的错误信息（请求层 reject 出来的是带 tips 的对象），
 * 没有可用信息时退化为默认文案
 */
function normalizeError(err, defaultMsg) {
  if (err && (isValidStr(err.tips) || isValidStr(err.errMsg) || isValidStr(err.message))) {
    return err;
  }
  return createError(defaultMsg);
}

/**
 * 从接口返回中取授权地址，兼容两种结构：
 * { data: "https://..." } 或 { data: { uri: "https://..." } }
 */
function getUriFromRes(res) {
  const data = res ? res.data : null;
  if (typeof data === "string") return data;
  if (data && typeof data.uri === "string") return data.uri;
  if (res && typeof res.uri === "string") return res.uri;
  return "";
}

/**
 * 从接口返回中取 token，兼容两种结构：
 * { data: "token" } 或 { token: "token" }
 */
function getTokenFromRes(res) {
  const data = res ? res.data : null;
  if (typeof data === "string") return data;
  if (data && typeof data.token === "string") return data.token;
  if (res && typeof res.token === "string") return res.token;
  return "";
}

/**
 * 解析 a=b&c=d 形式的字符串
 */
function parseQueryString(str) {
  const result = {};
  if (!str) return result;
  const index = str.indexOf("?");
  // hash 路由：#/login?code=xxx 取 ? 之后；没有 ? 也不像参数串则视为无参数
  const queryStr = index > -1 ? str.substr(index + 1) : str;
  if (!queryStr || queryStr.indexOf("=") === -1) return result;

  queryStr.split("&").forEach((pair) => {
    if (!pair) return;
    const i = pair.indexOf("=");
    const key = i > -1 ? pair.substr(0, i) : pair;
    const value = i > -1 ? pair.substr(i + 1) : "";
    try {
      result[decodeURIComponent(key)] = decodeURIComponent(value);
    } catch (e) {
      result[key] = value;
    }
  });
  return result;
}

/**
 * 获取当前地址上的单点参数
 * 兼容 ? 后面的参数，也兼容 hash 路由 #/login?code=xxx 的形式
 */
export function getSsoQueryParams() {
  const query = {};
  Object.assign(query, parseQueryString(window.location.search));
  Object.assign(query, parseQueryString(window.location.hash));
  return query;
}

/**
 * 是否属于 cfmmc 授权登录请求：loginType == 13 或已带 code + state
 */
export function isCfmmcSsoQuery(query) {
  const data = query || {};
  const hasCode = isValidStr(data.code) && isValidStr(data.state);
  return String(data.loginType) === "13" || hasCode;
}

/**
 * 写入登录态，localStorage / sessionStorage 双写（与项目原有登录保持一致）
 */
export function setToken(token) {
  common.sessionset(TOKEN_KEY, token);
  common.localset(TOKEN_KEY, token);
}

/**
 * 获取第三方授权地址，并当前页跳转到第三方登录页
 * @param {Object} params 附加参数，默认 platform: 'pc'
 * @returns {Promise<string>} 授权地址
 */
export async function openThirdPartyLogin(params) {
  const query = Object.assign({ platform: "pc" }, params || {});
  let res;
  try {
    res = await http.getConn("user.cfmmcSsoGetState")(query);
  } catch (err) {
    // 请求层已处理业务失败，这里保留后端原因给页面展示
    throw normalizeError(err, "获取第三方登录地址失败");
  }

  const uri = getUriFromRes(res);
  if (!isValidStr(uri)) {
    throw createError("获取第三方登录地址失败");
  }

  // 当前页跳转（不新开标签页）
  window.open(uri, "_self");
  return uri;
}

/**
 * 用第三方回跳的 code / state 换取本系统 token，并写入本地缓存
 * @param {Object} params { code, state }
 * @returns {Promise<string>} token
 */
export async function fetchTokenByCallback(params) {
  const data = params || {};
  const code = data.code;
  const state = data.state;

  if (!isValidStr(code) || !isValidStr(state)) {
    throw createError("code 或 state 缺失");
  }

  let res;
  try {
    res = await http.getConn("user.cfmmcSsoCallback")({
      code: code,
      state: state,
    });
  } catch (err) {
    throw normalizeError(err, "获取 token 失败");
  }

  const token = getTokenFromRes(res);
  if (!isValidStr(token)) {
    throw createError("获取 token 失败");
  }

  setToken(token);
  return token;
}

/**
 * 一站式处理 cfmmc 授权登录
 * @param {Object} query { loginType, code, state }
 * @returns {Promise<boolean>} true：token 已就绪，可继续后续登录流程；false：已跳转第三方
 */
export async function handleCfmmcSsoLogin(query) {
  const data = query || {};
  const hasCode = isValidStr(data.code) && isValidStr(data.state);

  if (!hasCode && !isCfmmcSsoQuery(data)) {
    throw createError("非 cfmmc 授权登录请求");
  }

  // 1. 第三方已回跳：用 code / state 换 token
  if (hasCode) {
    await fetchTokenByCallback({ code: data.code, state: data.state });
    return true;
  }

  // 2. 首次进入：拿授权地址并跳转第三方，页面即将离开
  await openThirdPartyLogin({ platform: "pc" });
  return false;
}

/**
 * 错误信息精简展示：tips -> message -> errMsg -> JSON 字符串
 */
export function briefErr(err) {
  if (!err) return "未知错误";
  if (typeof err === "string") return err;
  if (isValidStr(err.tips)) return err.tips;
  if (isValidStr(err.message)) return err.message;
  if (isValidStr(err.errMsg)) return err.errMsg;
  try {
    return JSON.stringify(err);
  } catch (e) {
    return String(err);
  }
}

export default {
  openThirdPartyLogin,
  fetchTokenByCallback,
  handleCfmmcSsoLogin,
  getSsoQueryParams,
  isCfmmcSsoQuery,
  setToken,
  briefErr,
};
